"use client";

import { ShieldCheck } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";

/**
 * Privacy Policy page content (WSP-1008).
 *
 * This is a deliberately honest placeholder, not the full legal policy: no
 * privacy policy content existed anywhere in the codebase before this fix, and
 * fabricating compliance-sounding legal text (cookie handling, data retention,
 * third-party sharing, GDPR/CCPA claims, etc.) without the business actually
 * standing behind it would be worse than the 404 it replaces. This states only
 * what's generically true and safe (we collect what you give us, we use it to
 * respond to you, we don't sell it) and points visitors to a real contact
 * channel for anything more specific, until the full policy is ready to
 * publish. All copy is driven by next-intl (privacyContent namespace).
 */
export function PrivacyContent() {
  const t = useTranslations('privacyContent');
  const summaryItems = t.raw('summaryItems') as string[];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12 text-white">
      <p className="text-white/80 leading-relaxed mb-8">{t('intro')}</p>

      <div className="bg-white/5 border border-white/10 rounded-lg p-6 mb-8">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-yellow-500 mb-4">
          <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          {t('summaryTitle')}
        </h2>
        <ul className="space-y-3 list-disc list-inside text-white/80">
          {summaryItems.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </div>

      <div className="border-t border-white/10 pt-8">
        <h2 className="text-lg font-semibold text-white mb-2">{t('contactTitle')}</h2>
        <p className="text-white/80 leading-relaxed mb-4">{t('contactBody')}</p>
        <Link
          href="/about/contact"
          className="inline-flex items-center text-yellow-500 hover:text-yellow-400 underline underline-offset-4"
        >
          {t('contactCta')}
        </Link>
      </div>
    </div>
  );
}
